import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, Clock, MessageSquare, Calendar, Globe } from "lucide-react";
import { useState } from "react";
import globalNetwork from "../assets/global-network.jpg";
import Section from "../components/Section";
import SectionHeading from "../components/SectionHeading";
import PageHero from "../components/PageHero";

const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

const Contact = () => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <div>
      <PageHero
        eyebrow="Contact Us"
        title={<>Get in Touch with <span className="energy-gradient-text">Our Team</span></>}
        description="Have questions about our energy projects, partnership opportunities, or existing account? Our team is available to help you Monday through Friday, 9:00 AM – 6:00 PM GMT."
        backgroundImage={globalNetwork}
      />

      <Section>
        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Info */}
          <motion.div {...fadeUp} className="space-y-6">
            <div>
              <h2 className="font-display text-2xl font-bold mb-2">Reach Out Anytime</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Whether you're an existing partner or exploring participation for the first time, our team is ready to answer your questions and provide detailed information about our renewable energy infrastructure.
              </p>
            </div>

            <div className="space-y-4">
              {[
                { icon: Mail, label: "Email Support", value: "info@aerovolt.energy", sub: "Response within 4 hours" },
                { icon: Phone, label: "Phone", value: "+44 20 7946 0958", sub: "Mon-Fri, 9AM-6PM GMT" },
                { icon: MapPin, label: "Head Office", value: "12th Floor, One Canada Square", sub: "Canary Wharf, London E14 5AB, UK" },
                { icon: Clock, label: "Business Hours", value: "Monday – Friday", sub: "9:00 AM – 6:00 PM GMT" },
                { icon: MessageSquare, label: "Live Chat", value: "Available on Dashboard", sub: "For existing partners" },
                { icon: Calendar, label: "Site Visit Requests", value: "By appointment", sub: "Available for Velocity & Apex partners" },
              ].map((c, i) => (
                <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }} className="flex items-start gap-4 p-4 rounded-xl glass-card glass-card-hover transition-all duration-500">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <c.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-display text-xs tracking-widest uppercase text-primary mb-0.5">{c.label}</div>
                    <div className="text-sm font-medium">{c.value}</div>
                    <div className="text-xs text-muted-foreground">{c.sub}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="p-4 rounded-xl glass-card">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-primary" />
                <span className="font-display text-xs tracking-widest uppercase text-primary">Regional Offices</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <span>🇪🇸 Madrid, Spain</span>
                <span>🇦🇪 Dubai, UAE</span>
                <span>🇸🇬 Singapore</span>
                <span>🇲🇦 Casablanca, Morocco</span>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            {...fadeUp}
            onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }}
            className="p-8 rounded-2xl glass-card"
          >
            {submitted ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6 animate-glow-pulse">
                  <Send className="w-10 h-10 text-primary" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-3">Message Sent Successfully!</h3>
                <p className="text-muted-foreground text-sm mb-6">Thank you for your interest. Our team will review your message and respond within 4 business hours.</p>
                <button type="button" onClick={() => setSubmitted(false)} className="text-primary text-sm font-medium hover:underline">Send Another Message</button>
              </div>
            ) : (
              <>
                <h3 className="font-display text-xl font-bold mb-6">Send Us a Message</h3>
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">First Name *</label>
                      <input placeholder="John" required className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Last Name *</label>
                      <input placeholder="Doe" required className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Email Address *</label>
                    <input type="email" placeholder="john@example.com" required className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Phone Number</label>
                    <input type="tel" placeholder="+44 XXX XXX XXXX" className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground transition-colors" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Inquiry Type</label>
                    <select className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary">
                      <option>General Inquiry</option>
                      <option>Partnership Opportunity</option>
                      <option>Investment Information</option>
                      <option>Technical Support</option>
                      <option>Site Visit Request</option>
                      <option>Media & Press</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Your Message *</label>
                    <textarea placeholder="Tell us how we can help you..." rows={5} required className="w-full px-4 py-3 rounded-lg bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground resize-none transition-colors" />
                  </div>
                  <button type="submit" className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-all glow-box hover:glow-box-hover flex items-center justify-center gap-2">
                    Send Message <Send className="w-4 h-4" />
                  </button>
                  <p className="text-[10px] text-muted-foreground text-center">By submitting, you agree to our Privacy Policy and Terms of Service.</p>
                </div>
              </>
            )}
          </motion.form>
        </div>
      </Section>
    </div>
  );
};

export default Contact;
